/**
 * Import contrôlé de documents client — Phase 1 mock/local (BT-023).
 *
 * Aucun vrai fichier n'est stocké : seules des MÉTADONNÉES sont manipulées.
 * Tout document préparé démarre au statut « reçu — validation requise ».
 * Aucun document n'est marqué officiel ou validé automatiquement.
 */

/** Statut de validation initial imposé à tout import réel. */
export const IMPORT_INITIAL_VALIDATION_STATUS = "recu_validation_requise";

/** Sensibilité par défaut d'un document importé. */
export const IMPORT_DEFAULT_SENSITIVITY = "confidentiel";

/** Message obligatoire affiché avant confirmation d'import. */
export const IMPORT_PREVIEW_NOTICE_FR =
  "Ce document sera ajouté comme reçu — validation requise. Il ne sera pas considéré officiel tant qu'une autorité compétente ne l'aura pas validé.";
export const IMPORT_PREVIEW_NOTICE_EN =
  "This document will be added as received — validation required. It will not be considered official until a competent authority has validated it.";

/** Message obligatoire affiché dans l'aperçu. */
export const IMPORT_PREVIEW_OFFICIAL_NOTICE_FR =
  "Aucun document importé ne sera considéré officiel automatiquement. Le statut initial demeure reçu — validation requise.";
export const IMPORT_PREVIEW_OFFICIAL_NOTICE_EN =
  "No imported document will be considered official automatically. The initial status remains received — validation required.";

/**
 * File d'import (métadonnées seulement). Exemples [DÉMO].
 * @type {import('./types.js').DocumentImportEntry[]}
 */
export const demoDocumentImportQueue = [
  {
    id: "import-demo-01",
    fichier_nom: "[DÉMO] certificat-grade-exemple.pdf",
    fichier_type: "application/pdf",
    taille_affichee: "— (métadonnée démo)",
    categorie_proposee: "autorite",
    type_document_propose: "preuve_grade",
    sensibilite_proposee: "tres_confidentiel",
    dossier_lie_propose: "dossier-auth-responsable",
    module_lie: "Shotokan Black Dragon",
    statut_import: "apercu_genere",
    statut_validation_initial: IMPORT_INITIAL_VALIDATION_STATUS,
    source: "import client",
    ajoute_par: "[DÉMO] admin",
    date_ajout: "2026-06-20",
    avertissements: ["warn-demo-01", "warn-demo-02"],
    notes: "[DÉMO] Exemple de métadonnée — aucun fichier réel stocké.",
    demo: true,
  },
  {
    id: "import-demo-02",
    fichier_nom: "[DÉMO] reglement-dojo-exemple.pdf",
    fichier_type: "application/pdf",
    taille_affichee: "— (métadonnée démo)",
    categorie_proposee: "dojo_general",
    type_document_propose: "autre",
    sensibilite_proposee: "interne",
    dossier_lie_propose: null,
    module_lie: "Dojo général",
    statut_import: "brouillon",
    statut_validation_initial: IMPORT_INITIAL_VALIDATION_STATUS,
    source: "import client",
    ajoute_par: "[DÉMO] admin",
    date_ajout: "2026-06-22",
    avertissements: ["warn-demo-03"],
    notes: "[DÉMO] Document général — aucun dossier lié.",
    demo: true,
  },
];

/**
 * Avertissements d'import (règles de prudence).
 * @type {import('./types.js').DocumentImportWarning[]}
 */
export const demoDocumentImportWarnings = [
  {
    id: "warn-demo-01",
    import_id: "import-demo-01",
    niveau: "attention",
    message: "Document sensible (très confidentiel) — accès restreint requis en Phase 2.",
    bloquant: false,
    action_recommandee: "Limiter la visibilité à Hanshi/Admin.",
  },
  {
    id: "warn-demo-02",
    import_id: "import-demo-01",
    niveau: "info",
    message: "Preuve officielle non validée — statut reçu — validation requise imposé.",
    bloquant: false,
    action_recommandee: "Faire valider par une autorité compétente.",
  },
  {
    id: "warn-demo-03",
    import_id: "import-demo-02",
    niveau: "attention",
    message: "Aucun dossier lié — le document ne sera rattaché à aucune preuve.",
    bloquant: false,
    action_recommandee: "Lier à un dossier de preuves si applicable.",
  },
];

/**
 * Plan du futur backend sécurisé (pédagogique, non implémenté).
 * @type {import('./types.js').SecureBackendBlueprint}
 */
export const secureBackendBlueprint = {
  id: "secure-backend-blueprint-v1",
  storage: {
    fr: "Stockage fichier hors repo, chiffré au repos et en transit.",
    en: "File storage outside the repo, encrypted at rest and in transit.",
  },
  requirements: [
    { key: "auth", fr: "Authentification réelle", en: "Real authentication" },
    { key: "users_roles", fr: "Gestion utilisateurs / rôles", en: "User / role management" },
    { key: "permissions", fr: "Permissions par document", en: "Per-document permissions" },
    { key: "storage_offrepo", fr: "Stockage fichier hors repo", en: "File storage outside the repo" },
    { key: "encryption_rest", fr: "Chiffrement au repos", en: "Encryption at rest" },
    { key: "encryption_transit", fr: "Chiffrement en transit", en: "Encryption in transit" },
    { key: "audit_immutable", fr: "Audit trail non modifiable", en: "Immutable audit trail" },
    { key: "versioning", fr: "Versionnement documents", en: "Document versioning" },
    { key: "backups", fr: "Sauvegardes", en: "Backups" },
    { key: "deletion", fr: "Suppression contrôlée", en: "Controlled deletion" },
    { key: "export", fr: "Export contrôlé", en: "Controlled export" },
    { key: "access_logs", fr: "Logs d'accès", en: "Access logs" },
    { key: "consent", fr: "Consentement", en: "Consent" },
    { key: "separation", fr: "Séparation public / interne / confidentiel / très confidentiel", en: "Public / internal / confidential / highly confidential separation" },
  ],
  roles: [
    { role: "Hanshi/Admin", fr: "Voir et valider documents sensibles", en: "View and validate sensitive documents" },
    { role: "Sensei", fr: "Voir documents liés à ses demandes", en: "View documents linked to their requests" },
    { role: "Sempai", fr: "Voir documents requis, pas valider", en: "View required documents, cannot validate" },
    { role: "Étudiant", fr: "Voir résumé, pas fichiers sensibles", en: "View summary, not sensitive files" },
    { role: "Parent", fr: "Accès limité aux documents de son enfant", en: "Limited access to their child's documents" },
    { role: "Auditeur futur", fr: "Voir journal sans modifier", en: "View log without modifying" },
  ],
  migration: [
    { step: 1, fr: "Figer le modèle de données", en: "Freeze the data model" },
    { step: 2, fr: "Créer l'API documents", en: "Create the documents API" },
    { step: 3, fr: "Créer le stockage sécurisé", en: "Create secure storage" },
    { step: 4, fr: "Migrer les métadonnées", en: "Migrate metadata" },
    { step: 5, fr: "Importer les fichiers hors repo", en: "Import files outside the repo" },
    { step: 6, fr: "Activer les permissions", en: "Enable permissions" },
    { step: 7, fr: "Activer l'audit trail immuable", en: "Enable immutable audit trail" },
    { step: 8, fr: "Désactiver localStorage pour documents sensibles", en: "Disable localStorage for sensitive documents" },
  ],
};
